// React
import { useContext } from "react"

// UI
import { cn } from "@/lib/utils"
import { Card } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { Button } from "@/components/Button"

// Context
import { RedirectContext } from "@/context/redirectContext"

// Translations
import { useTrans } from "@/i18n"

// Environment variables
import { EXTERNAL_PLATFORM_ZONHUB_INVESTMENT } from "@/lib/env"

// Icons
import { FiArrowRight, FiChevronDown, FiChevronUp } from "@/lib/icons"

// Redux
import { useDispatch, useSelector } from "@/state/hooks"
import {
	UserPreferencesState,
	setShowInvestmentBanner,
} from "@/state/features/userPreferencesSlice"

/**
 * InvestmentOpportunityBanner
 * @returns
 */
export function InvestmentOpportunityBanner({
	className = "",
}: {
	className?: any
}) {
	// Translations
	const t = useTrans("investments")

	// Context
	const { redirect } = useContext(RedirectContext)

	// Redux state
	const dispatch = useDispatch()
	const showInvestmentBanner = useSelector(
		({ userPreferences }: { userPreferences: UserPreferencesState }) =>
			userPreferences.showInvestmentBanner,
	)

	// `showInvestmentBanner` here means "banner body is expanded" (visible).
	// Default is true so first-time users actually see the CTA.
	const isExpanded = showInvestmentBanner
	const bodyId = "investment-opportunity-banner-body"

	return (
		<Card className={cn("relative w-full bg-yellow-50", className)}>
			<button
				type="button"
				onClick={() =>
					dispatch(setShowInvestmentBanner(!showInvestmentBanner))
				}
				className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
				aria-expanded={isExpanded}
				aria-controls={bodyId}
				aria-label={!isExpanded ? "Expand banner" : "Collapse banner"}
			>
				{!isExpanded ? (
					<FiChevronDown className="size-5" />
				) : (
					<FiChevronUp className="size-5" />
				)}
			</button>

			<Heading as="h2" styleAs="h5" className="sm:truncate">
				{t("investments:banner.opportunities.title")}
			</Heading>

			<div id={bodyId} hidden={!isExpanded}>
				<div className="my-4">
					<p className="text-sm text-gray-500">
						{t("investments:banner.opportunities.copy")}
					</p>
				</div>
				<Button
					variant="secondary"
					className="group inline-flex items-center gap-2"
					onClick={() => {
						redirect(EXTERNAL_PLATFORM_ZONHUB_INVESTMENT)
					}}
				>
					{t("investments:banner.opportunities.cta")}
					<FiArrowRight
						className="transition-transform group-hover:translate-x-1"
						aria-hidden
					/>
				</Button>
			</div>
		</Card>
	)
}
